import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Loader2, PlayCircle, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { ATTENDANCE_LABELS, SESSION_STATUS_LABELS } from "@/config/trainingCatalog";

const DAY_MS = 86400000;

function groupSessions(items) {
  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const weekEnd = new Date(today.getTime() + (7 - today.getDay()) * DAY_MS).toISOString().slice(0, 10);
  return {
    today: items.filter((s) => s.date === todayIso),
    week: items.filter((s) => s.date > todayIso && s.date <= weekEnd),
    upcoming: items.filter((s) => s.date > weekEnd),
  };
}

function SessionRow({ s, showRecord }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border p-3" data-testid={`schedule-session-${s.id}`}>
      <div className="min-w-[150px]">
        <p className="font-medium text-slate-900">{s.child?.child_name}</p>
        <p className="text-xs text-slate-500 font-mono">{s.child?.case_id} · {s.programme_number}</p>
      </div>
      <div className="text-xs text-slate-600">
        <p>{s.date}{s.planned_start_time ? ` · ${s.planned_start_time}` : ""}</p>
        <p>{s.direction === "outbound" ? "Home → School" : s.direction === "return" ? "School → Home" : "Other journey"}</p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {s.status === "draft" && <Badge className="bg-amber-100 text-amber-800 border-transparent">DRAFT</Badge>}
        <Badge className="bg-blue-50 text-blue-700 border-transparent">{SESSION_STATUS_LABELS[s.status]}</Badge>
        <Link to={`/sessions/${s.id}`} data-testid={`schedule-open-${s.id}`}
          className="inline-flex h-11 items-center gap-1 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover transition-colors duration-200">
          {s.status === "draft" ? <><PlayCircle className="h-4 w-4" /> Continue</> : showRecord ? <><PlayCircle className="h-4 w-4" /> Record</> : "Open"}
        </Link>
      </div>
    </div>
  );
}

export default function SchedulePage() {
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState(null);
  const isManager = hasPermission("programmes:manage");

  useEffect(() => {
    api.get("/schedule").then((r) => setItems(r.data.items)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const groups = items ? groupSessions(items) : null;

  return (
    <div className="space-y-4" data-testid="schedule-page">
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
        {isManager ? "My schedule" : "My schedule"}
      </h1>
      <p className="text-sm text-slate-600">
        Signed in as {user?.name}. You can only see your own scheduled and draft sessions.
      </p>

      {!groups ? (
        <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Loading schedule…</div>
      ) : (
        <>
          {[
            ["today", "Today"], ["week", "This week"], ["upcoming", "Upcoming"],
          ].map(([key, label]) => (
            <Card key={key} data-testid={`schedule-group-${key}`}>
              <CardHeader><CardTitle className="text-base flex items-center gap-2"><CalendarDays className="h-4 w-4 text-primary" /> {label} ({groups[key].length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {groups[key].length === 0 ? (
                  <p className="text-sm text-slate-600">Nothing {key === "today" ? "today" : "here"}.</p>
                ) : (
                  groups[key].map((s) => <SessionRow key={s.id} s={s} showRecord />)
                )}
              </CardContent>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

export function NewSessionButton({ programmeId, onCreated, testid }) {
  const [saving, setSaving] = useState(false);
  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/programmes/${programmeId}/sessions`, {});
      toast.success(`Session ${data.session_number} created`);
      onCreated?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <button type="button" data-testid={testid} onClick={create} disabled={saving}
      className="inline-flex h-11 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-brand-hover transition-colors duration-200">
      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} New training session
    </button>
  );
}
